import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import type { Driver } from '@logistech/ui-shared';

// Aligné sur les schemas Zod du fleet-service (routes/drivers/create|update.controller.ts)
const schema = z.object({
  firstName: z.string().trim().min(1, 'Prénom requis').max(60),
  lastName: z.string().trim().min(1, 'Nom requis').max(60),
  licenseNumber: z.string().trim().min(1, 'N° de permis requis').max(30),
  licenseExpiry: z.string(),
  phone: z.string().max(20),
  email: z.union([z.literal(''), z.string().email('Email invalide')]),
  hireDate: z.string(),
});

export type DriverFormData = z.infer<typeof schema>;

const toIsoDay = (d: string) => new Date(`${d}T00:00:00.000Z`).toISOString();

/** Champs vides → undefined (création) ou null (modification, pour effacer la valeur). */
export const toDriverPayload = (d: DriverFormData, emptyAs: undefined | null) => ({
  firstName: d.firstName,
  lastName: d.lastName,
  licenseNumber: d.licenseNumber,
  licenseExpiry: d.licenseExpiry ? toIsoDay(d.licenseExpiry) : emptyAs,
  phone: d.phone.trim() || emptyAs,
  email: d.email.trim() || emptyAs,
  hireDate: d.hireDate ? toIsoDay(d.hireDate) : emptyAs,
});

const dayValue = (iso: string | null | undefined) => (iso ? iso.slice(0, 10) : '');

interface Props {
  initial?: Driver;
  submitLabel: string;
  pending: boolean;
  onSubmit: (data: DriverFormData) => void;
  onCancel?: () => void;
}

export function DriverForm({ initial, submitLabel, pending, onSubmit, onCancel }: Props) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<DriverFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      firstName: initial?.firstName ?? '',
      lastName: initial?.lastName ?? '',
      licenseNumber: initial?.licenseNumber ?? '',
      licenseExpiry: dayValue(initial?.licenseExpiry),
      phone: initial?.phone ?? '',
      email: initial?.email ?? '',
      hireDate: dayValue(initial?.hireDate),
    },
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="firstName">Prénom *</Label>
          <Input id="firstName" {...register('firstName')} placeholder="Ibrahima" />
          {errors.firstName && <p className="text-xs text-danger mt-1">{errors.firstName.message}</p>}
        </div>
        <div>
          <Label htmlFor="lastName">Nom *</Label>
          <Input id="lastName" {...register('lastName')} placeholder="Bah" />
          {errors.lastName && <p className="text-xs text-danger mt-1">{errors.lastName.message}</p>}
        </div>
        <div>
          <Label htmlFor="licenseNumber">N° de permis *</Label>
          <Input id="licenseNumber" {...register('licenseNumber')} placeholder="GN-PC-2019-004512" />
          {errors.licenseNumber && <p className="text-xs text-danger mt-1">{errors.licenseNumber.message}</p>}
        </div>
        <div>
          <Label htmlFor="licenseExpiry">Expiration du permis</Label>
          <Input id="licenseExpiry" type="date" {...register('licenseExpiry')} />
        </div>
        <div>
          <Label htmlFor="phone">Téléphone</Label>
          <Input id="phone" {...register('phone')} placeholder="+224622000000" />
        </div>
        <div>
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" {...register('email')} />
          {errors.email && <p className="text-xs text-danger mt-1">{errors.email.message}</p>}
        </div>
        <div>
          <Label htmlFor="hireDate">Date d’embauche</Label>
          <Input id="hireDate" type="date" {...register('hireDate')} />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        {onCancel && <Button variant="outline" type="button" onClick={onCancel}>Annuler</Button>}
        <Button type="submit" disabled={pending}>{pending ? 'Enregistrement…' : submitLabel}</Button>
      </div>
    </form>
  );
}
